import {
  safeLanguageUrlPrefix,
  safeCurrentLanguage,
  languagePrefixes,
} from './drupal-services';

/**
 * Get URL Parameters.
 *
 * @param {string} location
 *   URL location.
 * @param {string} name
 *   URL parameter name.
 *
 * @returns {string}
 *   URL parameter value.
 */
export const getUrlParameter = (location: string, name: string): string => {
  name = name.replace(/\[/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  const results = regex.exec(location);
  return results === null
    ? ''
    : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

type UrlParameter = {
  name: string;
  value: string;
};

/**
 * Get Multiple URL Parameters
 *
 * @param {array} urlParameters
 *   Array of url parameter keys.
 * @return {array}
 *   Array of key, value pairs of URL parameters.
 */
export const getMultipleUrlParameters = (urlParameters: string[]) => {
  const parameters: UrlParameter[] = [];
  if (urlParameters.length > 0) {
    urlParameters.forEach((name: string) => {
      const value = getUrlParameter(window.location.href, name);
      parameters.push({ name: name, value: value });
    });
  }
  return parameters;
};

/**
 * Language Prefix Check.
 *
 * @param url
 * @returns
 */
const languagePrefixCheck = (url: string): boolean => {
  let prefixFound = false;
  languagePrefixes.forEach((prefix) => {
    if (url.indexOf(`/${prefix}/`) > -1) {
      prefixFound = true;
    }
  });
  return prefixFound;
};

/**s
 * Localize Links.
 * Localize groups of links for non-US English pages by parsing passed markup
 * for links. Used for two column description.
 */
export const localizeLinks = (markup: string): string => {
  if (safeCurrentLanguage() === 'en') {
    return markup;
  }

  const siteOrigin = window.location.origin;
  const element = document.createElement('div');
  element.innerHTML = markup;
  const links = element.querySelectorAll('a');
  if (links && links.length > 0) {
    links.forEach((link) => {
      const { href } = link;
      if (href.indexOf(siteOrigin) > -1) {
        let url = href.replace(siteOrigin, '');
        if (href.indexOf(url) > -1 && !languagePrefixCheck(url)) {
          url = `/${safeLanguageUrlPrefix()}${url}`.replace('//', '/');
          link.href = url;
        }
      }
    });
  }
  return element.innerHTML;
};

/**
 * Check for duplicate locales and remove them from links.
 */
export const checkForDuplicateLocales = (): void => {
  const links: NodeListOf<HTMLAnchorElement> = document.querySelectorAll('a');
  if (links.length > 0) {
    links.forEach((link) => {
      if (link.href.indexOf(window.location.origin) > -1) {
        const urlArray = link.href
          .replace(window.location.origin, '')
          .split('/');
        if (
          urlArray[1] === safeCurrentLanguage() &&
          urlArray[1] === urlArray[2]
        ) {
          const sanitizedUrl = link.href
            .replace(window.location.origin, '')
            .replace(safeLanguageUrlPrefix(), '');
          link.href = sanitizedUrl;
        }
      }
    });
  }
};

/**
 * Outbound Link Checker.
 */
export const outboundLinkChecker = (
  url: string | null | undefined
): boolean => {
  const inboundLinks = [location.hostname, '/node/', '<front>'];
  let outbound = true;
  if (url) {
    if (url[0] === '/') {
      return false;
    }

    inboundLinks.forEach((link: string) => {
      if (url?.indexOf(link) > -1) {
        outbound = false;
      }
    });
  }
  return outbound;
};

/**
 * Internal Link Formatter.
 */
export const internalLinkFormatter = (
  url: string | null | undefined
): string => {
  if (url) {
    return url.replace('internal:', '').replace('entity:', '');
  }
  return '';
};

/**
 * Format string for URL parameter.
 */
export const formatUrlParameter = (parameter: string | null) => {
  if (!parameter) {
    return null;
  }
  return `${parameter}`.toLowerCase().replaceAll(/\s/g, '-');
};

/**
 * Locale Url Check.
 */
export const localeUrlCheck = (url: string): string => {
  let safeUrl = url;
  if (safeCurrentLanguage() !== 'en' && url.indexOf('/node/') > -1) {
    if (!outboundLinkChecker(url)) {
      safeUrl = safeUrl.replace(window.location.origin, '');
      const urlArray = safeUrl.split('/');
      if (
        safeCurrentLanguage() !== 'es-419' &&
        !urlArray.includes(safeCurrentLanguage())
      ) {
        safeUrl = `/${safeCurrentLanguage()}${safeUrl}`;
      } else if (
        safeCurrentLanguage() === 'es-419' &&
        !urlArray.includes('es-xl')
      ) {
        safeUrl = `/es-xl${safeUrl}`;
      }
    }
  }
  return safeUrl;
};

/**
 * Update URL Parameters.
 *
 * This works with URL parameters being passed by either appending or removing
 * comma separated values.  This is not meant for incrementing, decrementing,
 * or replacing a single URL parameter value.
 *
 * @param {array} urlParameters
 *   Collection of parameters in a given URL.
 * @param {string} key
 *   URL filter parameter.
 * @param {string} type
 *   Filter type.
 *
 * @return {string}
 *   Updated and formatted URL based of current filter parameters.
 */
export const updateUrlParameters = (
  urlParameters: string[],
  key: string,
  type: string
): string => {
  const parameters = getMultipleUrlParameters(urlParameters);
  let url = 'limit=10';
  if (parameters.length > 0) {
    parameters.forEach((parameter) => {
      const { name, value } = parameter;

      if (type === name) {
        const currentFilters = value.split(',');
        if (currentFilters.length > 0 && currentFilters[0] === '') {
          currentFilters.shift();
        }

        const filterIndex = currentFilters.indexOf(key);
        if (filterIndex !== -1) {
          currentFilters.splice(filterIndex, 1);
          url =
            currentFilters.length > 0
              ? `${url}&${name}=${currentFilters.join(',')}`
              : url.replace(`&${name}=`, ``);
        } else {
          url =
            value !== ''
              ? `${url}&${name}=${value},${key}`
              : `${url}&${name}=${key}`;
        }
      } else {
        url = value !== '' ? `${url}&${name}=${value}` : url;
      }
    });
  }
  return url;
};
